import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { object, TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { generateInvoice } from "../../redux/actions/timesheet.actions";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import CreateIcon from "@mui/icons-material/Create";
import { green } from "@mui/material/colors";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { storeStateType } from "../../redux/store";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers-pro";
import FacilityInputField from "../CalendarForms/InputsComponents/FacilityInputField";

dayjs.extend(customParseFormat);

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  title: string;
  filterData: any;
}

const formSchema = object({
  invoice_date: z.string(),
  due_date: z.string(),
  facility: z
    .object({
      id: z.string(),
      facility_name: z.string(),
      depth: z.number(),
      parentId: z.string().optional().nullable(),
      matchTerms: z.string().array(),
    })
    .nullable(),
  user_role: z.string().nullable(),
});

type FormInput = TypeOf<typeof formSchema>;

export function TimesheetInvoice(props: Props) {
  const { handleCloseDialog } = props;
  const dispatch = useDispatch();
  const { save } = useSelector(
    (state: storeStateType) => state.employeetimesheet
  );

  const globalSettings = useSelector(
    (state: storeStateType) => state?.sitesetting?.settings
  );
  const loading = useSelector((state: storeStateType) => state.UI.loading);

  const [roleTypeList, setRoleTypeList] = useState<
    { label: string; id: string }[]
  >([]);
  const {
    control,
    formState: { errors },
    setValue,
    setError,
    handleSubmit,
    reset,
  } = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  });

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
    reset({
      invoice_date: "",
      due_date: "",
      facility: null,
      user_role: "",
    });
  }, [handleCloseDialog, reset]);

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save, handleClose]);

  useEffect(() => {
    if (globalSettings) {
      let userRoles: { label: string; id: string }[] = [];
      globalSettings.forEach((item: any) => {
        if (item["config_name"] === "site") {
          const configvalue = JSON.parse(item["config_value"]);
          if (configvalue) {
            userRoles = configvalue["user_role"]
              ?.split(/\r?\n/)
              ?.map((roleType: string) => {
                return { label: roleType, id: roleType };
              });
          }
        }
      });
      setRoleTypeList(userRoles);
    }
  }, [globalSettings]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    const formData = new FormData();
    let error = false;
    formData.append("invoice_date", data.invoice_date);
    formData.append("due_date", data.due_date);
    if (data.facility?.id) {
      formData.append("facility", data.facility.id);
    } else {
      error = true;
      setError("facility", { message: "Facility is required" });
    }
    if (data.user_role) {
      formData.append("user_role", data.user_role);
    }
    if (error) {
      return false;
    }
    dispatch<any>(generateInvoice(formData, props.filterData));
  };
  return (
    <Dialog open={props.isDialogOpened} onClose={handleClose} fullWidth>
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="companyform"
          name="companyform"
          noValidate
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="invoice_date"
                render={({ field: { onChange, value } }) => (
                  <>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label="Invoice Date"
                      onChange={(valueObj) => {
                        onChange(valueObj?.format("YYYY-MM-DD"));
                      }}
                      value={value ? moment(value) : null}
                      format="YYYY-MM-DD"
                    />
                    <FormHelperText error={!!errors["invoice_date"]}>
                      {errors["invoice_date"]
                        ? errors["invoice_date"].message
                        : ""}
                    </FormHelperText>
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="due_date"
                render={({ field: { onChange, value } }) => (
                  <>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label="Due date"
                      onChange={(valueObj) => {
                        onChange(valueObj?.format("YYYY-MM-DD"));
                      }}
                      value={value ? moment(value) : null}
                      format="YYYY-MM-DD"
                    />
                    <FormHelperText error={!!errors["invoice_date"]}>
                      {errors["invoice_date"]
                        ? errors["invoice_date"].message
                        : ""}
                    </FormHelperText>
                  </>
                )}
              />
            </Grid>
          </Grid>
          <Grid item my={2}>
            <FacilityInputField
              label={`Select Facilty`}
              variableName="facility"
              control={control}
              errors={errors}
              showChildrenOnly={true}
              defaultValue={""}
              parentId={""}
              setValue={setValue}
              isLoadFacilities={true}
              variant="outlined"
            />
          </Grid>
          <Grid container>
            <FormControl
              fullWidth
              variant="outlined"
              // error={!!errors['user_role']}
            >
              {/* <InputLabel id="role-select-label">{"Role Type"}</InputLabel> */}

              <Controller
                name={"user_role"}
                control={control}
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    // disablePortal
                    fullWidth
                    id="roletype"
                    onChange={(e, value) =>
                      // handleRegionChange(params?.row?.id, value?.id ?? "")
                      onChange(value?.id ?? "")
                    }
                    options={roleTypeList ?? []}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === value.id;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User Role"
                        InputProps={{
                          ...params.InputProps,
                        }}
                      />
                    )}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <DialogActions>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                type="submit"
                form="companyform"
                variant="contained"
                color="success"
                startIcon={<CreateIcon />}
                disabled={loading}
              >
                Create
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
            <Button
              onClick={handleClose}
              variant="contained"
              color="error"
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
